import { Trans } from "../utils/i18n";
import React from "react";
import { z } from "zod";
import { validatePhoneNumber } from "../Shared/FormField";

export const OrderEntryConfigReturnOrderDetailsSchema = z.object({
    ordernumber: z.string().min(1),
    orderEmail: z.string().email().min(1),
});

export const OrderEntryConfigReturnReturnItemsSchema = z.object({
    parent_order_number: z.string().min(1),
    products: z
        .array(
            z
                .object({
                    id: z.coerce.number().int(),
                    amount: z.union([z.string(), z.number()]).optional(),
                    problem_1: z.union([z.string(), z.number()]).optional(),
                    isReturn: z.boolean(),
                })
                .refine(
                    (item) => {
                        if (!item.isReturn) return true;
                        return item.amount !== undefined && item.amount > 0;
                    },
                    {
                        message: "Quantity must be greater than 0",
                    }
                )
                .refine(
                    (item) => {
                        if (!item.isReturn) return true;
                        return item.problem_1 !== undefined;
                    },
                    {
                        message: "Please select a reason",
                    }
                )
        )
        .min(1)
        .refine(
            (products) => {
                const hasNoReturnItemSelected = products.every(
                    (item) => !item.isReturn
                );

                if (hasNoReturnItemSelected) {
                    return false;
                }

                return true;
            },
            {
                path: ["products", "0", "isReturn"],
                message: "Please select at least one item to return",
            }
        ),
});

export const OrderEntryConfigReturnAddressSchema = z.object({
    name: z.string().min(1),
    email: z.string().email().min(1),
    phone: z.string().min(1).regex(RegExp(validatePhoneNumber)),
    street: z.string().min(1),
    housenumber: z.string().min(1),
    zip: z.string().min(1),
    city: z.string().min(1),
    country_id: z.coerce.number(),
});

export const OrderEntryConfigReturnConfirmationchema = z.object({
    terms_of_service: z.coerce.boolean().refine((value) => value === true),
});

export const OrderEntryConfigReturnSchema =
    OrderEntryConfigReturnOrderDetailsSchema.merge(
        OrderEntryConfigReturnReturnItemsSchema
    )
        .merge(OrderEntryConfigReturnAddressSchema)
        .merge(OrderEntryConfigReturnConfirmationchema);

export const orderEntryConfigReturn = {
    schema: OrderEntryConfigReturnSchema,
    sizes: {
        forth: 3,
        third: 4,
        half: 6,
        full: 12,
    },
    enableStepper: true,
    heading: <Trans i18nKey="orderEntry.basicDataAlt">Basic data</Trans>,
    groupsClassName: "py-8",
    prepareSubmitData: (values) => {
        return {
            ...values,
            // Filter out products that are not selected for return
            products: values.products.filter(product => product.isReturn),
        };
    },
    groups: [
        {
            id: "order_details",
            heading: (
                <Trans i18nKey="orderEntry.return.orderDetails">
                    Order details
                </Trans>
            ),
            fields: [
                {
                    "size": "full",
                    "component": "requesttypeselect"
                },
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h3 className="m-0">
                                <Trans
                                    i18nKey={
                                        "orderEntry.return.orderDetails"
                                    }
                                >
                                    Order Details
                                </Trans>
                            </h3>
                            <p>
                                <Trans
                                    i18nKey={
                                        "orderEntry.return.orderDetails.description"
                                    }
                                >
                                    Please enter your order number and email
                                    address. We will then check if the order is
                                    eligible for a return.
                                </Trans>
                            </p>
                        </>
                    ),
                },
                {
                    size: "full",
                    component: "enterreturnorderdetails",
                },
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h3 className="m-0">
                                <Trans
                                    i18nKey={
                                        "returnOrder.selectItems.title"
                                    }
                                >
                                    Select items
                                </Trans>
                            </h3>
                            <p>
                                <Trans
                                    i18nKey={
                                        "returnOrder.selectItems.description"
                                    }
                                >
                                    Please select the items you want to return and the reason for the return.
                                </Trans>
                            </p>
                        </>
                    ),
                    conditionFunction: (values, orderEntryState) => {
                        return (
                            values.products && values.products.length > 0
                        );
                    },
                },
                {
                    size: "full",
                    component: "selectreturnitems",
                    conditionFunction: (values, orderEntryState) => {
                        return (
                            values.products && values.products.length > 0
                        );
                    },
                },
            ],
        },
        {
            heading: (
                <Trans i18nKey="orderEntry.addressContact">
                    Contact information
                </Trans>
            ),
            description: (
                <Trans i18nKey="orderEntry.addressContact.description">
                    Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed
                    diam nonumy eirmod tempor invidunt ut labore et dolore magna
                    aliquyam erat, sed diam voluptua.
                </Trans>
            ),
            id: "address",
            fields: [
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h2 className="mb-0">
                                <Trans i18nKey={"returnOrder.selectItems.title"}>
                                    Items Selected for Return
                                </Trans>
                            </h2>
                        </>
                    ),
                },
                {
                    size: "full",
                    component: "selectreturnitems",
                    conditionFunction: (values, orderEntryState) => {
                        return (
                            values.products && values.products.length > 0
                        );
                    },
                    isViewMode: true,
                },
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h2>
                                <Trans i18nKey={"orderEntry.contactInfo"}>
                                    Contact info
                                </Trans>
                            </h2>
                        </>
                    ),
                },
                {
                    name: "name",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.name">
                            Name
                        </Trans>
                    ),
                    placeholder: (
                        <Trans i18nKey="orderEntry.addressContact.name">
                            Name
                        </Trans>
                    ),
                    size: "half",
                    type: "text",
                    required: true,
                },
                {
                    name: "email",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.email">
                            E-Mail Address
                        </Trans>
                    ),
                    placeholder: (
                        <Trans i18nKey="orderEntry.addressContact.email">
                            E-Mail Address
                        </Trans>
                    ),
                    size: "half",
                    type: "email",
                    required: true,
                },
                {
                    name: "phone",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.phone">
                            Phone
                        </Trans>
                    ),
                    placeholder: (
                        <Trans i18nKey="orderEntry.addressContact.phoneNumber">
                            Phone number
                        </Trans>
                    ),
                    size: "full",
                    type: "phoneinput",
                    required: true,
                },
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h2>
                                <Trans i18nKey={"orderEntry.addressInfo"}>
                                    Address info
                                </Trans>
                            </h2>
                        </>
                    ),
                },
                {
                    size: "full",
                    component: "addressautocomplete",
                },
                {
                    name: "zip",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.zip">
                            ZIP
                        </Trans>
                    ),
                    size: "half",
                    required: true,
                },
                {
                    name: "city",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.city">
                            City
                        </Trans>
                    ),
                    size: "half",
                    required: true,
                },
                {
                    name: "country_id",
                    label: (
                        <Trans i18nKey="orderEntry.addressContact.country">
                            Country
                        </Trans>
                    ),
                    placeholder: (
                        <Trans i18nKey="orderEntry.addressContact.country.placeholder">
                            Please choose
                        </Trans>
                    ),
                    size: "full",
                    type: "select",
                    options: [
                        {
                            value: "de",
                            label: (
                                <Trans i18nKey="orderEntry.addressContact.germany">
                                    Germany
                                </Trans>
                            ),
                        },
                        {
                            value: "ch",
                            label: (
                                <Trans i18nKey="orderEntry.addressContact.switzerland">
                                    Switzerland
                                </Trans>
                            ),
                        },
                    ],
                    required: true,
                    onlyCurrentCountry: true,
                },
            ],
            conditionFunction: (values, orderEntryState) => {
                const orderDetailsValidationResult =
                    OrderEntryConfigReturnOrderDetailsSchema.safeParse(values);
                const productsValidationResult =
                    OrderEntryConfigReturnReturnItemsSchema.safeParse(values);

                return (
                    orderDetailsValidationResult.success &&
                    productsValidationResult.success
                );
            },
        },
        {
            heading: (
                <Trans i18nKey="common.checkout">Confirmation / Summary</Trans>
            ),
            description: (
                <Trans i18nKey="orderEntry.termsandconditions.description"></Trans>
            ),
            id: "terms",
            fields: [
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h2 className="mb-0">
                                <Trans i18nKey={"returnOrder.selectItems.title"}>
                                    Items Selected for Return
                                </Trans>
                            </h2>
                        </>
                    ),
                },
                {
                    size: "full",
                    component: "selectreturnitems",
                    conditionFunction: (values, orderEntryState) => {
                        return (
                            values.products && values.products.length > 0
                        );
                    },
                    isViewMode: true,
                },
                {
                    size: "full",
                    component: "callback",
                    callback: (values) => (
                        <>
                            <h2 style={{ marginBottom: 0 }}>
                                <Trans
                                    i18nKey={"orderEntry.verifyAccountToPay"}
                                >
                                    Verify your information
                                </Trans>
                            </h2>
                        </>
                    ),
                },
                {
                    size: "full",
                    component: "verify_phone_number",
                },
                {
                    name: "terms_of_service",
                    label: (
                        <Trans i18nKey="orderEntry.termsandconditions.label">
                            I have read and accept the
                            <a
                                href={route("termsandconditions")}
                                target={"_blank"}
                            >
                                Terms & Conditions
                            </a>
                            and the
                            <a href={route("privacy")} target={"_blank"}>
                                Privacy Statement
                            </a>
                        </Trans>
                    ),
                    size: "full",
                    type: "checkbox",
                    required: true,
                },
            ],
            conditionFunction: (values, orderEntryState) => {
                const orderDetailsValidationResult =
                    OrderEntryConfigReturnOrderDetailsSchema.safeParse(values);
                const productsValidationResult =
                    OrderEntryConfigReturnReturnItemsSchema.safeParse(values);
                const addressValidationResult =
                    OrderEntryConfigReturnAddressSchema.safeParse(values);

                return (
                    orderDetailsValidationResult.success &&
                    productsValidationResult.success &&
                    addressValidationResult.success
                );
            },
        },
    ],
    actions: [
        {
            fields: [
                {
                    text: <Trans i18nKey="orderEntry.send">Send</Trans>,
                    size: "half",
                    component: "button",
                    action: "next",
                    color: "secondary",
                    conditionFunction: (values, orderEntryState) => {
                        const validationResult =
                            OrderEntryConfigReturnSchema.safeParse(values);

                        return (
                            validationResult.success &&
                            orderEntryState?.verificationCodeResponse
                        );
                    },
                },
            ],
        },
    ],
};
